import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, IconBtn, Switcher, type SwitcherOption } from '@/components/primitives';
import { useViewport } from '@/lib/viewport';
import { useAuth } from '@/lib/auth';
import { COUNTRIES, ROLE_OPTIONS, useScope } from '@/lib/scope';
import { UserMenu } from './UserMenu';

interface TopNavProps {
  onOpenCmdK: () => void;
  onOpenSidebar: () => void;
}

const iconBtnRelativeStyle: CSSProperties = { position: 'relative' };

export function TopNav({ onOpenCmdK, onOpenSidebar }: TopNavProps) {
  const navigate = useNavigate();
  const { isMobile, isCompact, vw } = useViewport();
  const { user } = useAuth();
  const {
    country,
    setCountry,
    warehouseId,
    setWarehouseId,
    role,
    setRole,
    countryWarehouses,
    currentWarehouse,
  } = useScope();

  const whOptions: SwitcherOption[] = countryWarehouses.map((w) => ({
    value: w.id,
    label: w.code,
    sublabel: w.name,
    badge: w.is_active ? 'ACTIVE' : 'INACTIVE',
  }));
  const cOptions: SwitcherOption[] = COUNTRIES.map((c) => ({
    value: c.code,
    label: c.code,
    sublabel: c.name,
  }));

  const showFullSwitchers = vw >= 1024;
  const showRolePill = vw >= 1180;
  const showCmdKLabel = vw >= 960;
  const showSubtitle = vw >= 560;
  const showLogoText = vw >= 480;

  const scopeLabel = country + ' · ' + (currentWarehouse ? currentWarehouse.code : '—');

  return (
    <header
      style={{
        height: 56,
        background: '#fff',
        borderBottom: '1px solid var(--bx-border-muted)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 12px',
        gap: 10,
        flexShrink: 0,
      }}
    >
      {isMobile && (
        <IconBtn name="grid" onClick={onOpenSidebar} title="Menu" style={{ flexShrink: 0 }} />
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          paddingRight: isCompact ? 0 : 14,
          borderRight: isCompact ? 'none' : '1px solid var(--bx-border-muted)',
          height: '100%',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: 'var(--bx-primary)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          W
        </div>
        {showLogoText && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              lineHeight: 1.1,
              minWidth: 0,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: 13.5,
                color: 'var(--bx-text-heading)',
                whiteSpace: 'nowrap',
              }}
            >
              Kế hoạch nhân sự
            </span>
            {showSubtitle && (
              <span
                style={{
                  fontSize: 10,
                  color: 'var(--bx-text-muted)',
                  letterSpacing: '0.05em',
                  whiteSpace: 'nowrap',
                }}
              >
                v2 · Boxme Vận hành
              </span>
            )}
          </div>
        )}
      </div>

      {showFullSwitchers ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginLeft: 4,
            minWidth: 0,
          }}
        >
          <Switcher
            icon="globe"
            label="Quốc gia"
            value={country}
            options={cOptions}
            onChange={(v) => setCountry(v as typeof country)}
            width={88}
          />
          <Switcher
            icon="warehouse"
            label="Kho"
            value={warehouseId}
            options={whOptions}
            onChange={setWarehouseId}
            width={180}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={onOpenSidebar}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            height: 36,
            padding: '0 10px',
            background: '#fff',
            border: '1px solid var(--bx-border)',
            borderRadius: 'var(--bx-radius-md)',
            cursor: 'pointer',
            fontFamily: 'var(--bx-font-sans)',
            fontSize: 12.5,
            color: 'var(--bx-text-dark)',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flexShrink: 1,
            overflow: 'hidden',
          }}
        >
          <Icon name="warehouse" size={13} style={{ color: 'var(--bx-text-muted)' }} />
          <span
            style={{
              fontWeight: 600,
              fontFamily: 'var(--bx-font-mono)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {scopeLabel}
          </span>
          <Icon name="chevron-down" size={11} style={{ color: 'var(--bx-text-muted)' }} />
        </button>
      )}

      {showCmdKLabel ? (
        <button
          type="button"
          onClick={onOpenCmdK}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            height: 36,
            padding: '0 10px',
            background: 'var(--bx-surface-2)',
            border: '1px solid var(--bx-border-muted)',
            borderRadius: 'var(--bx-radius-md)',
            cursor: 'pointer',
            color: 'var(--bx-text-muted)',
            fontSize: 12.5,
            minWidth: 220,
            maxWidth: 320,
          }}
        >
          <Icon name="search" size={13} />
          <span
            style={{
              flex: 1,
              textAlign: 'left',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Tìm kế hoạch, nhân sự, tham số…
          </span>
          <kbd
            style={{
              fontFamily: 'var(--bx-font-mono)',
              fontSize: 10.5,
              background: '#fff',
              border: '1px solid var(--bx-border-muted)',
              padding: '2px 6px',
              borderRadius: 4,
              color: 'var(--bx-text-body)',
            }}
          >
            ⌘K
          </kbd>
        </button>
      ) : (
        <IconBtn
          name="search"
          onClick={onOpenCmdK}
          title="Tìm · ⌘K"
          style={{ marginLeft: 'auto' }}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        <IconBtn
          name="help"
          onClick={() => navigate('/help')}
          title="Trợ giúp & Hướng dẫn"
        />
        <button type="button" title="Thông báo" style={notificationBtnStyle}>
          <Icon name="bell" size={15} />
          <span
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 7,
              height: 7,
              background: 'var(--bx-error)',
              borderRadius: 9999,
              border: '2px solid #fff',
            }}
          />
        </button>
        {showRolePill && (
          <>
            <div
              style={{
                width: 1,
                height: 24,
                background: 'var(--bx-border-muted)',
                margin: '0 6px',
              }}
            />
            <Switcher
              icon="shield"
              label="Vai trò"
              value={role}
              options={ROLE_OPTIONS.map((r) => ({ value: r.value, label: r.label }))}
              onChange={(v) => setRole(v as typeof role)}
              width={130}
            />
          </>
        )}
        <div
          style={{
            width: 1,
            height: 24,
            background: 'var(--bx-border-muted)',
            margin: '0 4px',
          }}
        />
        {user && <UserMenu user={user} compact={!showRolePill} />}
      </div>
    </header>
  );
}

const notificationBtnStyle: CSSProperties = {
  ...iconBtnRelativeStyle,
  width: 32,
  height: 32,
  border: '1px solid transparent',
  background: 'transparent',
  color: 'var(--bx-text-body)',
  borderRadius: 8,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};
